import type SQLite from 'react-native-sqlite-storage';
import { mapResultRows } from './offlineDatabase.rows';

export function getPendingMigrationVersions(
  currentVersion: number,
  targetVersion: number
): number[] {
  const normalizedCurrentVersion = Number.isFinite(currentVersion)
    ? Math.max(0, Math.trunc(currentVersion))
    : 0;
  const normalizedTargetVersion = Number.isFinite(targetVersion)
    ? Math.max(0, Math.trunc(targetVersion))
    : 0;

  if (normalizedCurrentVersion >= normalizedTargetVersion) {
    return [];
  }

  return Array.from(
    { length: normalizedTargetVersion - normalizedCurrentVersion },
    (_, index) => normalizedCurrentVersion + index + 1
  );
}

export async function getTableColumns(db: SQLite.SQLiteDatabase, tableName: string): Promise<string[]> {
  const tableInfo = await db.executeSql(`PRAGMA table_info(${tableName})`);
  return mapResultRows<string>(tableInfo[0], (row) => row.name);
}

export async function addColumnIfMissing(
  db: SQLite.SQLiteDatabase,
  tableName: string,
  columnName: string,
  columnDefinition: string
): Promise<boolean> {
  const columns = await getTableColumns(db, tableName);

  if (columns.includes(columnName)) {
    return false;
  }

  try {
    await db.executeSql(`ALTER TABLE ${tableName} ADD COLUMN ${columnDefinition}`);
  } catch (error) {
    if (isDuplicateColumnError(error)) {
      return false;
    }

    throw error;
  }

  return true;
}

function isDuplicateColumnError(error: unknown): boolean {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message?: unknown }).message)
      : String(error);

  return message.toLowerCase().includes('duplicate column');
}

export async function tableExists(db: SQLite.SQLiteDatabase, tableName: string): Promise<boolean> {
  const result = await db.executeSql(
    `SELECT name FROM sqlite_master WHERE type='table' AND name=?`,
    [tableName]
  );

  return result[0].rows.length > 0;
}
